var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Create, FileInput, SimpleForm, useCheckMinimumRequiredProps, useMutation, useNotify, useRedirect, } from 'react-admin';
import InputGuesser from './InputGuesser';
import Introspecter from './Introspecter';
const displayOverrideCode = (schema, fields) => {
    if (process.env.NODE_ENV === 'production')
        return;
    let code = 'If you want to override at least one input, paste this content in the <CreateGuesser> component of your resource:\n\n';
    code += `const ${schema.title}Create = props => (\n`;
    code += `    <CreateGuesser {...props}>\n`;
    fields.forEach((field) => {
        code += `        <InputGuesser source={"${field.name}"} />\n`;
    });
    code += `    </CreateGuesser>\n`;
    code += `);\n`;
    code += `\n`;
    code += `And don't forget update your <ResourceGuesser> component:\n`;
    code += `<ResourceGuesser name={"${schema.name}"} create={${schema.title}Create} />`;
    // eslint-disable-next-line no-console
    console.info(code);
};
export const IntrospectedCreateGuesser = (_a) => {
    var { fields, readableFields, writableFields, schema, schemaAnalyzer, resource, basePath, onSuccess, successMessage, onFailure, redirect: redirectTo = 'list', initialValues, validate, toolbar, margin, variant, submitOnEnter, warnWhenUnsavedChanges, sanitizeEmptyValues, simpleFormComponent, children } = _a, props = __rest(_a, ["fields", "readableFields", "writableFields", "schema", "schemaAnalyzer", "resource", "basePath", "onSuccess", "successMessage", "onFailure", "redirect", "initialValues", "validate", "toolbar", "margin", "variant", "submitOnEnter", "warnWhenUnsavedChanges", "sanitizeEmptyValues", "simpleFormComponent", "children"]);
    const [mutate] = useMutation();
    const notify = useNotify();
    const redirect = useRedirect();
    let inputChildren = React.Children.toArray(children);
    if (inputChildren.length === 0) {
        inputChildren = writableFields.map((field) => (React.createElement(InputGuesser, { key: field.name, source: field.name })));
        displayOverrideCode(schema, writableFields);
    }
    const hasFileField = inputChildren.some((child) => typeof child === 'object' && 'type' in child && child.type === FileInput);
    const save = useCallback((values) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const response = yield mutate({
                type: 'create',
                resource,
                payload: {
                    data: Object.assign(Object.assign({}, values), { extraInformation: { hasFileField } }),
                },
            }, { returnPromise: true });
            const success = onSuccess !== null && onSuccess !== void 0 ? onSuccess : (({ data: newRecord }) => {
                notify(successMessage || 'ra.notification.created', 'info', {
                    smart_count: 1,
                });
                redirect(redirectTo, basePath, newRecord.id, newRecord);
            });
            success(response);
            return undefined;
        }
        catch (mutateError) {
            const submissionErrors = schemaAnalyzer.getSubmissionErrors(mutateError);
            const failure = onFailure !== null && onFailure !== void 0 ? onFailure : ((error) => {
                let message = 'ra.notification.http_error';
                if (!submissionErrors) {
                    message =
                        typeof error === 'string' ? error : error.message || message;
                }
                let errorMessage;
                if (typeof error === 'string') {
                    errorMessage = error;
                }
                else if (error === null || error === void 0 ? void 0 : error.message) {
                    errorMessage = error.message;
                }
                notify(message, 'warning', {
                    _: errorMessage,
                });
            });
            failure(mutateError);
            if (submissionErrors) {
                return submissionErrors;
            }
            return {};
        }
    }), [
        mutate,
        hasFileField,
        resource,
        onSuccess,
        successMessage,
        onFailure,
        notify,
        redirect,
        redirectTo,
        basePath,
        schemaAnalyzer,
    ]);
    return (React.createElement(Create, Object.assign({ resource: resource, basePath: basePath }, props),
        React.createElement(SimpleForm, { save: save, initialValues: initialValues, validate: validate, toolbar: toolbar, margin: margin, variant: variant, submitOnEnter: submitOnEnter, warnWhenUnsavedChanges: warnWhenUnsavedChanges, sanitizeEmptyValues: sanitizeEmptyValues, component: simpleFormComponent }, inputChildren)));
};
const CreateGuesser = (props) => {
    useCheckMinimumRequiredProps('CreateGuesser', ['resource'], props);
    const { resource } = props, rest = __rest(props, ["resource"]);
    if (!resource) {
        return null;
    }
    return (React.createElement(Introspecter, Object.assign({ component: IntrospectedCreateGuesser, resource: resource }, rest)));
};
/* eslint-disable tree-shaking/no-side-effects-in-initialization */
CreateGuesser.propTypes = {
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    resource: PropTypes.string.isRequired,
};
/* eslint-enable tree-shaking/no-side-effects-in-initialization */
export default CreateGuesser;
//# sourceMappingURL=CreateGuesser.js.map